import en from '../translations/en.json';
import { locales } from '../data/locales';

const SUPPORTED_LOCALES: readonly string[] = locales.map(({ code }) => code);

interface TranslationDictionary { [key: string]: string }

function getInlineTranslations(): TranslationDictionary | null {
  if (!('document' in globalThis)) return null;
  const el = document.getElementById('app-i18n');
  if (!el || !el.textContent) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
}

function normalizeLocale(locale: string | null | undefined): string | null {
  const normalized = (locale || '').split('-')[0].toLowerCase();
  return SUPPORTED_LOCALES.includes(normalized) ? normalized : null;
}

function detectPathLocale(): string | null {
  if (!('window' in globalThis)) return null;
  const [, maybeLocale] = window.location.pathname.split('/');
  return normalizeLocale(maybeLocale);
}

function detectLocale(): string {
  if (!('document' in globalThis)) return 'en';
  return detectPathLocale() || normalizeLocale(document.documentElement.lang) || 'en';
}

const translations: TranslationDictionary = { ...en, ...(getInlineTranslations() ?? {}) };
let currentLocale = detectLocale();

export function getCurrentLocale(): string {
  return currentLocale;
}

export function t(key: string, fallback?: string): string {
  return translations[key] || fallback || key;
}

export function pluralCategory(locale: string, count: number): Intl.LDMLPluralRule {
  try {
    return new Intl.PluralRules(locale).select(count);
  } catch {
    return count === 1 ? 'one' : 'other';
  }
}

function formatCount(locale: string, count: number): string {
  try {
    return new Intl.NumberFormat(locale === 'ar' ? 'ar-u-nu-arab' : locale).format(count);
  } catch {
    return String(count);
  }
}

export function formatPluralTranslation(
  dictionary: Readonly<Record<string, string>>,
  locale: string,
  baseKey: string,
  oneKey: string,
  otherKey: string,
  count: number,
  placeholder = 'n',
): string {
  const category = pluralCategory(locale, count);
  const categoryKey = `${baseKey}_${category}`;
  const value = dictionary[categoryKey] || dictionary[category === 'one' ? oneKey : otherKey] || otherKey;
  return value.replace(`{${placeholder}}`, formatCount(locale, count));
}

export function tPlural(
  baseKey: string,
  oneKey: string,
  otherKey: string,
  count: number,
  placeholder = 'n',
): string {
  return formatPluralTranslation(translations, currentLocale, baseKey, oneKey, otherKey, count, placeholder);
}

function applyDocumentTranslations(): void {
  if (!('document' in globalThis)) return;

  document.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    const key = el.getAttribute('data-i18n');
    if (!key) return;
    const val = t(key);
    if (val && val !== key) el.textContent = val;
  });

  document.querySelectorAll<HTMLElement>('[data-i18n-html]').forEach((el) => {
    const key = el.getAttribute('data-i18n-html');
    if (!key) return;
    const val = t(key);
    if (val && val !== key) el.innerHTML = val;
  });

  document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[data-i18n-placeholder]').forEach((el) => {
    const key = el.getAttribute('data-i18n-placeholder');
    if (!key) return;
    const val = t(key);
    if (val && val !== key) el.placeholder = val;
  });

  document.querySelectorAll<HTMLElement>('[data-i18n-title]').forEach((el) => {
    const key = el.getAttribute('data-i18n-title');
    if (!key) return;
    const val = t(key);
    if (val && val !== key) el.title = val;
  });

  document.querySelectorAll<HTMLElement>('[data-i18n-tooltip]').forEach((el) => {
    const key = el.getAttribute('data-i18n-tooltip');
    if (!key) return;
    const val = t(key);
    if (val && val !== key) el.setAttribute('data-tooltip', val);
  });

  document.querySelectorAll<HTMLElement>('[data-i18n-aria-label]').forEach((el) => {
    const key = el.getAttribute('data-i18n-aria-label');
    if (!key) return;
    const val = t(key);
    if (val && val !== key) el.setAttribute('aria-label', val);
  });
}

export function initI18n(): void {
  currentLocale = detectLocale();
  applyDocumentTranslations();
}
